import { computed, onMounted, onUnmounted, readonly, ref } from 'vue'
import type { GuestTester, GuestTesterInput, TestSession, TestSessionDraft, UFPMScoreEntry } from '../lib/types'
import {
  addGuestToSession,
  addMemberToSession,
  clearSession,
  clearSessionConfirmMessage,
  clearSessionDraft,
  createEmptySession,
  draftCount,
  hasDrafts,
  loadSession,
  markGuestSaved as markGuestSavedInSession,
  markMemberSaved,
  removeMemberFromSession,
  saveSession,
  savedCount,
  setActiveMember,
  updateSessionDraft,
} from '../lib/testSession'

function dispatch(session: TestSession): void {
  try {
    window.dispatchEvent(new CustomEvent('ptr:ufpm-session-changed', { detail: { session } }))
  } catch { /* ignore */ }
}

export function useUFPMTestSession() {
  const session = ref<TestSession>(loadSession() ?? createEmptySession())

  function onExternalSessionChanged(event: Event): void {
    const next = (event as CustomEvent).detail?.session as TestSession | undefined
    if (next) session.value = next
  }

  onMounted(() => window.addEventListener('ptr:ufpm-session-changed', onExternalSessionChanged))
  onUnmounted(() => window.removeEventListener('ptr:ufpm-session-changed', onExternalSessionChanged))

  function persist(): void {
    saveSession(session.value)
    dispatch(session.value)
  }

  const activeMember = computed(() =>
    session.value.activeMemberId
      ? session.value.members[session.value.activeMemberId] ?? null
      : null
  )

  const sessionSavedCount = computed(() => savedCount(session.value))
  const sessionDraftCount = computed(() => draftCount(session.value))
  const sessionHasDrafts  = computed(() => hasDrafts(session.value))

  function addMember(memberId: string): void {
    session.value = addMemberToSession(session.value, memberId)
    persist()
  }

  function removeMember(memberId: string): void {
    session.value = removeMemberFromSession(session.value, memberId)
    persist()
  }

  function setActive(memberId: string): void {
    session.value = setActiveMember(session.value, memberId)
    persist()
  }

  function updateDraft(memberId: string, draft: TestSessionDraft): void {
    session.value = updateSessionDraft(session.value, memberId, draft)
    persist()
  }

  function clearDraft(memberId: string): void {
    session.value = clearSessionDraft(session.value, memberId)
    persist()
  }

  function markSaved(memberId: string): void {
    // Guard: don't double-save — if already saved, no-op
    if (session.value.members[memberId]?.completion === 'saved') return
    session.value = markMemberSaved(session.value, memberId)
    persist()
  }

  /** Adds a free-form guest tester; they become the active tester. */
  function addGuest(input: GuestTesterInput): GuestTester {
    const result = addGuestToSession(session.value, input)
    session.value = result.session
    persist()
    return result.guest
  }

  /** Stores a guest's score on the session (guests have no roster history). */
  function markGuestSaved(guestId: string, entry: UFPMScoreEntry): void {
    if (session.value.members[guestId]?.completion === 'saved') return
    session.value = markGuestSavedInSession(session.value, guestId, entry)
    persist()
  }

  function resetSession(): void {
    clearSession()
    session.value = createEmptySession()
    dispatch(session.value)
  }

  /** Returns true if cleared, false if user cancelled the confirm */
  function clearSessionWithConfirm(): boolean {
    const msg = clearSessionConfirmMessage(session.value)
    if (msg === null) {
      resetSession()
      return true
    }
    if (!window.confirm(msg)) return false
    resetSession()
    return true
  }

  return {
    session: readonly(session),
    activeMember,
    savedCount: sessionSavedCount,
    draftCount: sessionDraftCount,
    hasDrafts: sessionHasDrafts,
    addMember,
    removeMember,
    setActive,
    updateDraft,
    clearDraft,
    markSaved,
    addGuest,
    markGuestSaved,
    clearSessionWithConfirm,
  }
}
